import { derived, get } from "svelte/store";
import { countryStore } from "../stores/country";
import { isPrimaryCampaign } from "./countries";
import { languageStore, type Language } from "../stores/language";
import { makeLink } from "./links";
import { urls } from "./urls";

type input = {
  over18: string,
  agreedToAll: string,
  informationHandling: string,
  receiveCommercialInfo?: string
}

const labelCheckboxes = ({
  over18,
  agreedToAll,
  informationHandling,
  receiveCommercialInfo
}: input) => {
  return [
    {
      label: over18,
      identifier: "over18",
      mandatory: true,
      defaultValue: false
    },
    {
      label: agreedToAll,
      identifier: "agreedToAll",
      mandatory: true,
      defaultValue: false
    },
    {
      label: informationHandling,
      identifier: "informationHandling",
      mandatory: true,
      defaultValue: false
    },
    receiveCommercialInfo ? {
      label: receiveCommercialInfo,
      identifier: "receiveCommercialInfo",
      mandatory: false,
      defaultValue: false
    } : undefined
  ].filter(Boolean) as { label: string, identifier: string, mandatory: boolean, defaultValue: boolean }[];
}

export const labeledCheckboxes = derived([countryStore, languageStore], ([$country, $language]) => {
  const checkboxCopy: Record<Language, input> = {
    en: {
      over18: (
        isPrimaryCampaign
          ? 'I certify that I am a legal resident residing in the 50 United States or D.C., Canada (excluding Quebec), or Mexico who is'
          : 'I certify that I am a resident Chile, Guatemala, Nicaragua, El Salvador, Dominican Republic or Honduras'
      ) + `
        at least eighteen (18) years of age and that I am not an employee of Little Caesars Enterprises, Inc.,
        or any affiliated agency and that I have read and agree to the ${makeLink(get(urls).officialRules, "Official Rules")}.
        I also understand that, I agree to release and hold harmless Sponsor against any claim
        or cause of action arising out of the receipt or use of any reward.
      `,
      agreedToAll: $country === "US"
        ? `
          I have read and agree to the LittleCaesars.com ${makeLink(get(urls).termsOfService, "Terms of Service")},
          ${makeLink(get(urls).privacyPolicy, "Privacy Policy")}
          and ${makeLink(get(urls).usStatePrivacyPolicy, "US State Privacy Policy")}
        `
        : `
          I have read and agree to the LittleCaesars.com ${makeLink(get(urls).termsOfService, "Terms of Service")}
          and ${makeLink(get(urls).privacyPolicy, "Privacy Policy")}
        `,
      informationHandling: "I consent to having this website store my information for the promotional purpose of this program",
      receiveCommercialInfo: `
        I'm in! Please keep me in the loop on all things Little Caesars® - including exclusive offers and product updates. (optional)
        <p class="mt-2">
          I would like to receive commercial electronic information and offers from Little Caesars®
          (including emails and push notifications, which may be subject to fees charged by my wireless carrier),
          including on my wireless device. I may later unsubscribe.
          By clicking on this button, I have read and agree to the Little Caesars®
          ${makeLink(get(urls).incentiveLoyaltyProgramNotice, "Incentive/Loyalty Program Notice")}.
          My consent is sought by Little Caesar Enterprises, Inc. for itself and for Little Caesar of Canada ULC.
          Little Caesar Enterprises, Inc., 2211 Woodward Ave., Detroit, MI 48201; (${makeLink("mailto:privacy@lcecorp.com", "privacy@lcecorp.com")})
        </p>
      `
    },
    es: {
      over18: (
        isPrimaryCampaign
          ? 'Certifico que soy residente legal en los 50 Estados Unidos o D.C., Canadá (excluyendo Quebec), o México'
          : 'Certifico que soy residente de Chile, Guatemala, Nicaragua, El Salvador, República Dominicana u Honduras'
      ) + `
        con al menos dieciocho (18) años y que no soy empleado de Little Caesar Enterprises, Inc.,
        o cualquier agencia afiliada y que he leído y estoy de acuerdo con el
        ${makeLink(get(urls).officialRules, "Reglamento Oficial")}.
        También entiendo que acepto liberar y eximir de responsabilidad al Patrocinador
        frente a cualquier reclamación o causa de acción que surja de la recepción o uso de cualquier recompensa.
      `,
      agreedToAll: $country === "US"
        ? `
          He leído y estoy de acuerdo con los ${makeLink(get(urls).termsOfService, "Términos de Servicio")},
          ${makeLink(get(urls).privacyPolicy, "Política de Privacidad")}
          y ${makeLink(get(urls).usStatePrivacyPolicy, "Política de Privacidad de EU de LittleCaesars.com")}
        `
        : `
          He leído y estoy de acuerdo con los ${makeLink(get(urls).termsOfService, "Términos de Servicio")}
          y ${makeLink(get(urls).privacyPolicy, "Política de Privacidad")}.
        `,
      informationHandling: "Doy mi consentimiento para que este sitio web almacene mi información con fines promocionales de este programa",
      receiveCommercialInfo: `
        ¡Me apunto! Por favor mantenme informado sobre todo lo relacionado con Little Caesars® – incluyendo ofertas exclusivas y actualizaciones de productos. (opcional)
        <p class="mt-2">
          Deseo recibir información electrónica comercial y ofertas de Little Caesars® (incluyendo correos electrónicos y notificaciones,
          que pueden estar sujetas a tarifas aplicadas por mi operador de telefonía móvil), incluidas en mi dispositivo móvil.
          Es posible que me dé de baja más adelante.
          Al hacer clic en este botón, he leído y estoy de acuerdo con el
          ${makeLink(get(urls).incentiveLoyaltyProgramNotice, "Aviso sobre el Programa de Incentivos/Lealtad")} de Little Caesars®.
          Little Caesar Inc. solicita mi consentimiento para sí mismo y para Little Caesars de Canadá ULC.
          Little Caesar Enterprises, Inc., 2211 Woodward Ave., Detroit, MI 48201; (${makeLink("mailto:privacy@lcecorp.com", "privacy@lcecorp.com")})
        </p>
      `
    },
    fr: {
      over18: (
        isPrimaryCampaign
          ? 'Je certifie que je suis un résident légal vivant dans l\'un des 50 États américains ou à D.C., au Canada (à l\'exclusion de la province du Québec) ou au Mexique'
          : 'Je certifie que je suis un résident du Chili, du Guatemala, du Nicaragua, du Salvador, de la République Dominicaine ou du Honduras'
      ) + `
        que je suis âgé d'au moins dix-huit (18) ans,
        que je ne suis pas un employé de Little Caesar Enterprises, Inc., ni d'aucune agence affiliée et que j'ai lu et que j'accepte le
        ${makeLink(get(urls).officialRules, "règlement officiel")}.
        Je comprends également que je m'engage à dégager le commanditaire de toute responsabilité et à
        le tenir indemne de toute réclamation ou cause d'action découlant de la réception ou de l'utilisation de toute récompense.
      `,
      agreedToAll: $country === "US"
        ? `
          J'ai lu et j'accepte les ${makeLink(get(urls).termsOfService, "conditions d'utilisation")},
          la ${makeLink(get(urls).privacyPolicy, "politique de confidentialité sur LittleCaesars.ca")}
          et l'${makeLink(get(urls).usStatePrivacyPolicy, "US State Privacy Policy")}
        `
        : `
          J'ai lu et j'accepte les ${makeLink(get(urls).termsOfService, "conditions d'utilisation")}
          et la ${makeLink(get(urls).privacyPolicy, "politique de confidentialité sur LittleCaesars.ca")}.
        `,
      informationHandling: "Je consens à ce que ce site Web conserve mes renseignements dans le but promotionnel de ce programme.",
      receiveCommercialInfo: `
        Je suis partant! Veuillez me tenir informé de tout ce qui concerne Little Caesars®, y compris les offres exclusives et les mises à jour sur les produits (optionnel).
        <p class="mt-2">
          J'aimerais recevoir de l'information et des offres électroniques commerciales de Little Caesars®
          (y compris des courriels et des notifications poussées, lesquels peuvent être assujettis aux frais facturés par
          mon fournisseur de services sans fil), y compris sur mon appareil sans fil. Il est possible que je me désabonne par la suite.
          En cliquant sur ce bouton, j'ai lu et j'accepte l'
          ${makeLink(get(urls).incentiveLoyaltyProgramNotice, "Incentive/Loyalty Program Notice")} de Little Caesars®.
          Mon consentement est demandé par Little Caesar Enterprises, Inc. en son nom et en celui de Little Caesar of Canada SRI.
          Little Caesar Enterprises, Inc., 2211, Woodward Ave., Detroit, MI 48201; (${makeLink("mailto:privacy@lcecorp.com", "privacy@lcecorp.com")})
        </p>
      `
    }
  }

  const languageCheckboxCopy = checkboxCopy[$language];

  if (!isPrimaryCampaign) {
    delete languageCheckboxCopy.receiveCommercialInfo;
  }

  return labelCheckboxes(checkboxCopy[$language]);
});
